/**
 * Thin client for the Re-Engage backend, routed through the existing
 * Next proxy at /api/proxy/[...path].
 *
 * URL shape: /api/proxy/api/v1/dialer/re-engage/<rest>
 *
 * Why through the proxy: /api/v1/* has no Next route handler. Direct fetches
 * to /api/v1/dialer/re-engage/... return Next's default 404 HTML page.
 * The proxy authenticates via NextAuth, attaches X-Service-Token, and
 * forwards to the backend at BACKEND_URL.
 *
 * Why this helper exists: PR #317 hooks called /api/v1/* directly (broken).
 * Refactor to /api/proxy/api/v1/* (working) is the fix. Helper centralizes
 * URL composition so hooks never assemble proxy paths by hand. Issue #334
 * tracks the broader audit — ColumnMappingModal and others have the same
 * direct-call bug.
 *
 * Out of scope: Idempotency-Key (proxy strips it; see Issue #338),
 * timeouts (Issue #336), success-path logging (operator-invisible
 * from browser).
 */

const PROXY_PREFIX = "/api/proxy/api/v1/dialer/re-engage";

export function reEngageGet(path: string): Promise<Response> {
  return fetch(`${PROXY_PREFIX}${path}`, { method: "GET" });
}

/**
 * POST to a Re-Engage subpath. `path` defaults to "" so callers POSTing to
 * the namespace root (`useStartReEngageRun` → `/api/proxy/api/v1/dialer/re-engage`)
 * call `reEngagePost(undefined, body)` rather than the visually-confusing
 * `reEngagePost("", body)`.
 */
export function reEngagePost(
  path: string = "",
  body: unknown,
  options?: { idempotencyKey?: string }
): Promise<Response> {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };
  // Plan v6 Decision 5: client mints a fresh nonce per submit; reuses on
  // transport retry. Note Issue #338 — the proxy currently strips the
  // Idempotency-Key header. Until #338 lands, the wizard still records the
  // nonce as PostHog metadata so we can correlate replays in logs; setting
  // the header here means idempotency activates the moment #338 is fixed,
  // with no further code changes on this side.
  if (options?.idempotencyKey) {
    headers["Idempotency-Key"] = options.idempotencyKey;
  }
  return fetch(`${PROXY_PREFIX}${path}`, {
    method: "POST",
    headers,
    body: JSON.stringify(body),
  });
}
